/**
 * Share control for the article and product pages.
 *
 * Two paths, chosen by capability: the Web Share API where the browser has it (Safari,
 * most mobile) and a copy-to-clipboard panel built on `<details>` everywhere else — which
 * is why the same page offers an OS share sheet in Safari and a copy field in Chrome.
 *
 * Every path ends in feedback the reader can see. That is the part the stock Dawn
 * component gets wrong and bitesvitamins.com still gets wrong: it calls
 * `navigator.clipboard.writeText(...).then(...)` with no `catch`, so wherever the
 * async Clipboard API is refused — an iframe without `clipboard-write` such as the theme
 * editor preview, an unfocused document, a non-secure origin — Copy does nothing at all
 * and says nothing (QI TSK-82335).
 */
export class ShareButton extends HTMLElement {
  #initialised = false;

  connectedCallback() {
    // connectedCallback can fire again if the element is moved, and the section renderer
    // does move things; binding twice would double every handler.
    if (this.#initialised) {
      return;
    }

    this.#initialised = true;
    this.urlToShare = this.urlInput?.value || document.location.href;

    this.setupFallbackShare();

    if (navigator.share) {
      this.setupNativeShare();
    }
  }

  get nativeButton() {
    return this.querySelector('[data-share-native]');
  }

  get panel() {
    return this.querySelector('[data-share-panel]');
  }

  get summary() {
    return this.querySelector('[data-share-summary]');
  }

  get copyButton() {
    return this.querySelector('[data-share-copy]');
  }

  get closeButton() {
    return this.querySelector('[data-share-close]');
  }

  get message() {
    return this.querySelector('[data-share-message]');
  }

  get urlInput() {
    return this.querySelector('[data-share-url]');
  }

  /**
   * Hands off to the OS share sheet. A dismissed sheet rejects with AbortError and is not
   * a failure; anything else means the reader got nothing, so fall back to the copy panel
   * rather than logging where they cannot see it.
   */
  setupNativeShare() {
    this.panel.setAttribute('hidden', 'hidden');
    this.nativeButton.classList.remove('hidden');

    this.nativeButton.addEventListener('click', async () => {
      try {
        await navigator.share({url: this.urlToShare, title: document.title});
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.revealPanel();
        }
      }
    });
  }

  setupFallbackShare() {
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.panel.open = !this.panel.open;
    });

    // Scoped to the whole component, not just the panel. Scoping it to the panel meant a
    // click on the native button counted as "outside": the share rejection reopened the
    // panel in a microtask, then this listener received the same still-bubbling click and
    // shut it again, so the button looked completely dead.
    document.addEventListener('click', (event) => {
      if (this.contains(event.target) || !this.panel.open) {
        return;
      }

      this.panel.open = false;
    });

    // Clearing on close rather than in the close button's own handler covers every way
    // the panel can shut — the ✕, an outside click, Escape — so reopening Share never
    // shows a stale "Link copied" from an earlier visit.
    this.panel.addEventListener('toggle', () => {
      if (this.panel.open) {
        return;
      }

      this.clearMessage();
      this.summary.focus();
    });

    this.copyButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.copyToClipboard();
    });

    this.closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.panel.open = false;
    });
  }

  /** Swaps the native button for the copy panel when the OS share sheet fails to open. */
  revealPanel() {
    this.nativeButton.classList.add('hidden');
    this.panel.removeAttribute('hidden');
    this.panel.open = true;
  }

  /**
   * Copies the URL and always reports what happened: the success message when the link
   * reached the clipboard, otherwise the URL left selected with a prompt to copy it by
   * hand. Never claims a copy that did not occur, and never falls silent.
   */
  async copyToClipboard() {
    if (await this.writeToClipboard()) {
      this.showMessage(this.dataset.shareSuccess);
      this.closeButton.classList.remove('hidden');
      this.closeButton.focus();
      return;
    }

    this.urlInput.select();
    this.showMessage(this.dataset.shareManual, 'manual');
  }

  /**
   * @returns {Promise<boolean>} whether the URL actually reached the clipboard.
   */
  async writeToClipboard() {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(this.urlToShare);
        return true;
      } catch {
        // Refused — the async API is feature-detectable but not permission-detectable.
        // Fall through to the command the older browsers used, which is still allowed in
        // contexts the async API is not.
      }
    }

    this.urlInput.select();

    try {
      return document.execCommand('copy');
    } catch {
      return false;
    }
  }

  /**
   * @param {string} text
   * @param {'manual'} [variant] - keeps the URL field visible so it can still be copied
   */
  showMessage(text, variant) {
    this.message.textContent = text;
    this.message.classList.remove('hidden');
    this.message.classList.toggle('share-button__message--manual', variant === 'manual');
  }

  clearMessage() {
    this.message.textContent = '';
    this.message.classList.add('hidden');
    this.message.classList.remove('share-button__message--manual');
    this.closeButton.classList.add('hidden');
  }
}

if (!customElements.get('share-button')) {
  customElements.define('share-button', ShareButton);
}
